import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import heroImage from "../../assets/images/hero-ai.png";
import solution0Image from "../../assets/images/giai-phap-ai-0.png";
import solution1Image from "../../assets/images/giai-phap-ai-1.png";
import solution2Image from "../../assets/images/giai-phap-ai-2.png";
import solution3Image from "../../assets/images/giai-phap-ai-3.png";
import solution4Image from "../../assets/images/giai-phap-ai-4.png";
// @ts-ignore
import sectionItemService from '../../services/sectionItemService';
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';

const PAGE_SLUG = 'tich-hop-ai-tu-dong-hoa-doanh-nghiep';

export default function AIAutomationServicePage() {
  // Auto fetch SEO from database based on route
  useAutoSEO();

  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    solutions: false,
    benefits: false,
    cta: false,
  });
  const [sectionItems, setSectionItems] = useState<{
    solutions?: any[];
    benefits?: any[];
  }>({});
  const [loading, setLoading] = useState(true);

  const heroRef = useRef<HTMLElement>(null);
  const solutionsRef = useRef<HTMLElement>(null);
  const benefitsRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Add Google Fonts
    const link1 = document.createElement("link");
    link1.href = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap";
    link1.rel = "stylesheet";
    document.head.appendChild(link1);

    // Trigger hero animation
    setTimeout(() => setIsVisible(true), 100);

    return () => {
      document.head.removeChild(link1);
    };
  }, []);

  // Fetch section items from API
  useEffect(() => {
    const fetchSectionItems = async () => {
      try {
        setLoading(true);
        const response = await sectionItemService.getServicePage(PAGE_SLUG);
        if (response?.success && response.data) {
          setSectionItems(response.data);
        } else {
          console.warn('[AIAutomation] No data in response:', response);
          setSectionItems({});
        }
      } catch (error) {
        console.error('[AIAutomation] Error fetching section items:', error);
        setSectionItems({});
      } finally {
        setLoading(false);
      }
    };

    fetchSectionItems();
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "0px 0px -80px 0px",
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => ({ ...prev, [sectionName]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const refs = [
      { ref: heroRef, name: "hero" },
      { ref: solutionsRef, name: "solutions" },
      { ref: benefitsRef, name: "benefits" },
      { ref: ctaRef, name: "cta" },
    ];

    refs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div
      className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden bg-white text-gray-900"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {/* Hero Section */}
      <section
        ref={heroRef}
        data-section="hero"
        className="relative overflow-hidden py-32 px-10 min-h-[calc(100vh-100px)] flex items-center"
        style={{ backgroundColor: "#2a4b7c" }}
      >
        {/* Animated floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full opacity-20"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${
                  3 + Math.random() * 4
                }s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
          <div
            className={`z-10 flex flex-col gap-8 text-white transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-12"
            }`}
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full w-fit"
              style={{
                backgroundColor: "rgba(6, 182, 212, 0.2)",
                border: "1px solid rgba(6, 182, 212, 0.4)",
              }}
            >
              <i
                className="ri-sparkling-line text-sm"
                style={{ color: "#06b6d4" }}
              ></i>
              <span
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: "#06b6d4" }}
              >
                Tiên phong công nghệ 4.0
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black leading-[1.1] tracking-tight">
              Tối ưu hóa doanh nghiệp với sức mạnh{" "}
              <span
                className="text-white border-b-4"
                style={{ borderColor: "#06b6d4" }}
              >
                AI & Tự động hóa
              </span>
            </h1>
            <p className="text-lg md:text-xl text-blue-100 max-w-[500px] font-light leading-relaxed">
              Chúng tôi giúp doanh nghiệp ứng dụng AI thế hệ mới để tự động hóa
              quy trình, khai thác dữ liệu hiệu quả và tạo lợi thế cạnh tranh
              bền vững.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/lien-he"
                className="flex min-w-[180px] cursor-pointer items-center justify-center rounded-lg h-14 px-6 text-white text-base font-bold shadow-lg hover:-translate-y-1 transition-all"
                style={{
                  backgroundColor: "#06b6d4",
                  boxShadow: "0 4px 20px rgba(6, 182, 212, 0.2)",
                }}
              >
                <span>Liên hệ tư vấn</span>
              </Link>
              <a
                href="#solutions"
                className="flex min-w-[180px] cursor-pointer items-center justify-center rounded-lg h-14 px-6 text-white text-base font-bold backdrop-blur-sm border transition-all"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.2)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)")
                }
              >
                <span>Tìm hiểu thêm</span>
              </a>
            </div>
          </div>
          <div
            className={`relative hidden lg:block transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-12"
            }`}
          >
            <div
              className="w-full aspect-square rounded-full absolute blur-[120px] -top-20 -right-20 animate-pulse"
              style={{ backgroundColor: "rgba(6, 182, 212, 0.2)" }}
            ></div>
            <div
              className="w-full h-[500px] bg-center bg-no-repeat bg-contain relative z-10 transition-transform duration-700 hover:scale-105"
              style={{
                backgroundImage: `url(${heroImage})`,
                animation: "float-slow 6s ease-in-out infinite",
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section
        ref={solutionsRef}
        data-section="solutions"
        id="solutions"
        className="bg-white py-24"
      >
        <div className="max-w-[1280px] mx-auto px-10">
          <div
            className={`flex flex-col gap-2 mb-16 transition-all duration-1000 ${
              visibleSections.solutions
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <span
              className="font-bold tracking-widest text-sm uppercase"
              style={{ color: "#06b6d4" }}
            >
              Dịch vụ cốt lõi
            </span>
            <h2
              className="text-4xl font-bold leading-tight tracking-tight"
              style={{ color: "#2a4b7c" }}
            >
              Các giải pháp AI trọng tâm
            </h2>
            <div
              className="h-1.5 w-24 rounded-full"
              style={{ backgroundColor: "#06b6d4" }}
            ></div>
          </div>
          <div className="flex flex-col gap-24">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Đang tải...</p>
              </div>
            ) : (sectionItems.solutions || []).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Chưa có nội dung</p>
              </div>
            ) : (
              (sectionItems.solutions || []).map((solution, index) => {
                const solutionImages = [solution0Image, solution1Image, solution2Image, solution3Image, solution4Image];
                const isEven = index % 2 === 0;
                const delay = `${0.2 + index * 0.2}s`;
                
                // Parse description để tách phần mô tả, Khả năng và Giá trị
                const parseDescription = (desc: string) => {
                  const parts = desc.split(/Khả năng:|Giá trị:/);
                  let shortDesc = parts[0]?.trim() || '';
                  let khaNang = '';
                  let giaTri = '';
                  
                  if (desc.includes('Khả năng:')) {
                    const khaNangMatch = desc.match(/Khả năng:\s*([^.]*(?:\.[^G]*)?)/);
                    khaNang = khaNangMatch ? khaNangMatch[1].trim() : '';
                  }
                  
                  if (desc.includes('Giá trị:')) {
                    const giaTriMatch = desc.match(/Giá trị:\s*(.+)/);
                    giaTri = giaTriMatch ? giaTriMatch[1].trim() : '';
                  }
                  
                  return { shortDesc, khaNang, giaTri };
                };
                
                const { shortDesc, khaNang, giaTri } = parseDescription(solution.description || '');
                const displayKhaNang = khaNang || (solution.features && solution.features.length > 0 ? solution.features.join(', ') : '');
                
                return (
                  <div key={solution.id || index} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    {isEven ? (
                      <>
                        <div
                          className={`order-2 lg:order-1 flex flex-col gap-6 transition-all duration-1000 ${
                            visibleSections.solutions
                              ? "opacity-100 translate-x-0"
                              : "opacity-0 -translate-x-20"
                          }`}
                          style={{ transitionDelay: delay }}
                        >
                          <div
                            className="p-8 bg-white rounded-2xl border border-gray-100 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-pointer group"
                            style={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)" }}
                          >
                            <p
                              className="font-bold text-2xl mb-6 group-hover:text-[#06b6d4] transition-colors duration-300"
                              style={{ color: "#2a4b7c" }}
                            >
                              {solution.title}
                            </p>
                            {shortDesc && (
                              <p className="text-sm text-gray-500 mb-6">
                                {shortDesc}
                              </p>
                            )}
                            <div className="space-y-6">
                              {displayKhaNang && (
                                <div className="flex gap-4 group/item hover:translate-x-2 transition-transform duration-300">
                                  <i
                                    className="ri-calendar-check-line text-3xl group-hover/item:scale-110 transition-transform duration-300"
                                    style={{ color: "#06b6d4" }}
                                  ></i>
                                  <div>
                                    <p className="text-gray-600 text-base leading-normal">
                                      <strong style={{ color: "#2a4b7c" }}>
                                        Khả năng:
                                      </strong>{" "}
                                      {displayKhaNang}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {giaTri && (
                                <div className="flex gap-4 group/item hover:translate-x-2 transition-transform duration-300">
                                  <i
                                    className="ri-focus-3-line text-3xl group-hover/item:scale-110 transition-transform duration-300"
                                    style={{ color: "#06b6d4" }}
                                  ></i>
                                  <div>
                                    <p className="text-gray-600 text-base leading-normal">
                                      <strong style={{ color: "#2a4b7c" }}>Giá trị:</strong>{" "}
                                      {giaTri}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`order-1 lg:order-2 w-full aspect-video bg-cover rounded-2xl shadow-2xl overflow-hidden transition-all duration-1000 hover:scale-105 hover:shadow-[0_30px_60px_rgba(6,182,212,0.3)] ${
                            visibleSections.solutions
                              ? "opacity-100 translate-x-0"
                              : "opacity-0 translate-x-20"
                          }`}
                          style={{
                            backgroundImage: `url(${solution.image ? (solution.image.startsWith('http') ? solution.image : (solution.image.startsWith('/') ? solution.image : `/${solution.image}`)) : (solutionImages[index] || solution0Image)})`,
                            transitionDelay: `${parseFloat(delay) + 0.1}s`,
                          }}
                        ></div>
                      </>
                    ) : (
                      <>
                        <div
                          className={`w-full aspect-video bg-cover rounded-2xl shadow-2xl overflow-hidden transition-all duration-1000 hover:scale-105 hover:shadow-[0_30px_60px_rgba(6,182,212,0.3)] ${
                            visibleSections.solutions
                              ? "opacity-100 translate-x-0"
                              : "opacity-0 -translate-x-20"
                          }`}
                          style={{
                            backgroundImage: `url(${solution.image ? (solution.image.startsWith('http') ? solution.image : (solution.image.startsWith('/') ? solution.image : `/${solution.image}`)) : (solutionImages[index] || solution0Image)})`,
                            transitionDelay: delay,
                          }}
                        ></div>
                        <div
                          className={`flex flex-col gap-6 transition-all duration-1000 ${
                            visibleSections.solutions
                              ? "opacity-100 translate-x-0"
                              : "opacity-0 translate-x-20"
                          }`}
                          style={{ transitionDelay: `${parseFloat(delay) + 0.1}s` }}
                        >
                          <div
                            className="p-8 bg-white rounded-2xl border border-gray-100 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-pointer group"
                            style={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)" }}
                          >
                            <p
                              className="font-bold text-2xl mb-6 group-hover:text-[#06b6d4] transition-colors duration-300"
                              style={{ color: "#2a4b7c" }}
                            >
                              {solution.title}
                            </p>
                            {shortDesc && (
                              <p className="text-sm text-gray-500 mb-6">
                                {shortDesc}
                              </p>
                            )}
                            <div className="space-y-6">
                              {displayKhaNang && (
                                <div className="flex gap-4 group/item hover:translate-x-2 transition-transform duration-300">
                                  <i
                                    className="ri-global-line text-3xl group-hover/item:scale-110 transition-transform duration-300"
                                    style={{ color: "#06b6d4" }}
                                  ></i>
                                  <div>
                                    <p className="text-gray-600 text-base leading-normal">
                                      <strong style={{ color: "#2a4b7c" }}>
                                        Khả năng:
                                      </strong>{" "}
                                      {displayKhaNang}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {giaTri && (
                                <div className="flex gap-4 group/item hover:translate-x-2 transition-transform duration-300">
                                  <i
                                    className="ri-arrow-up-circle-line text-3xl group-hover/item:scale-110 transition-transform duration-300"
                                    style={{ color: "#06b6d4" }}
                                  ></i>
                                  <div>
                                    <p className="text-gray-600 text-base leading-normal">
                                      <strong style={{ color: "#2a4b7c" }}>Giá trị:</strong>{" "}
                                      {giaTri}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        ref={benefitsRef}
        data-section="benefits"
        className="text-white py-24 px-10"
        style={{ backgroundColor: "#2a4b7c" }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div
            className={`text-center mb-16 transition-all duration-1000 ${
              visibleSections.benefits
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-12"
            }`}
          >
            <h2 className="text-4xl font-bold mb-4">
              Vì sao doanh nghiệp chọn chúng tôi?
            </h2>
            <p
              className="max-w-2xl mx-auto"
              style={{ color: "rgba(191, 219, 254, 0.6)" }}
            >
              Chúng tôi kết hợp chuyên môn sâu về ngành và công nghệ tiên tiến
              nhất để mang lại hiệu quả vượt trội.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 relative">
            <div
              className="hidden lg:block absolute top-12 left-0 w-full h-px z-0"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
            ></div>

            {loading ? (
              <div className="col-span-4 text-center py-12">
                <div className="inline-block w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-white/80">Đang tải...</p>
              </div>
            ) : (sectionItems.benefits || []).length === 0 ? (
              <div className="col-span-4 text-center py-12">
                <p className="text-white/80">Chưa có nội dung</p>
              </div>
            ) : (
              (sectionItems.benefits || []).map((benefit, index) => (
              <div
                key={benefit.id || index}
                className={`flex flex-col items-center text-center gap-6 relative z-10 transition-all duration-1000 ${
                  visibleSections.benefits
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-16"
                }`}
                style={{ transitionDelay: `${0.2 + index * 0.1}s` }}
              >
                <div
                  className={`w-24 h-24 rounded-full flex items-center justify-center border-4 shadow-lg hover:scale-110 hover:rotate-12 transition-all duration-500 cursor-pointer ${
                    benefit.iconBg || 'bg-[#06b6d4]'
                  }`}
                  style={{
                    borderColor: "#2a4b7c",
                    boxShadow: "0 10px 25px rgba(6, 182, 212, 0.2)",
                    animation: "pulse-slow 3s ease-in-out infinite",
                  }}
                >
                  <i className={`${benefit.icon} text-4xl ${benefit.iconColor || benefit.color || 'text-white'}`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-2 whitespace-nowrap">{benefit.title}</h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "rgba(191, 219, 254, 0.6)" }}
                  >
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        data-section="cta"
        className="py-32 px-10 overflow-hidden relative"
        style={{ backgroundColor: "#f3f4f6" }}
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#2a4b7c 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        ></div>
        <div
          className={`max-w-[800px] mx-auto text-center flex flex-col items-center gap-8 relative z-10 transition-all duration-1000 ${
            visibleSections.cta
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-12"
          }`}
        >
          <h2
            className="text-5xl font-black tracking-tight"
            style={{ color: "#2a4b7c" }}
          >
            Sẵn sàng để AI hỗ trợ bạn?
          </h2>
          <p className="text-xl text-gray-600">
            Bắt đầu hành trình chuyển đổi số ngay hôm nay để không bỏ lỡ lợi thế
            cạnh tranh.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
            <Link
              to="/lien-he"
              className="flex min-w-[280px] cursor-pointer items-center justify-center rounded-xl h-16 px-8 text-white text-lg font-bold shadow-xl hover:scale-105 hover:-translate-y-2 transition-all duration-300 group"
              style={{
                backgroundColor: "#06b6d4",
                boxShadow: "0 20px 50px rgba(6, 182, 212, 0.3)",
                animation: "pulse-glow 2s ease-in-out infinite",
              }}
            >
              <span className="group-hover:scale-110 transition-transform duration-300">
                Đăng ký tư vấn miễn phí
              </span>
            </Link>
          </div>
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>
            Hỗ trợ kỹ thuật 24/7 sau khi triển khai
          </p>
        </div>
      </section>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-30px) translateX(10px);
            opacity: 0.4;
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-20px) scale(1.02);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            box-shadow: 0 10px 25px rgba(6, 182, 212, 0.2);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 15px 35px rgba(6, 182, 212, 0.4);
            transform: scale(1.05);
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 20px 50px rgba(6, 182, 212, 0.3);
          }
          50% {
            box-shadow: 0 25px 60px rgba(6, 182, 212, 0.5);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: #06b6d4;
          border-radius: 5px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #0891b2;
        }
      `}</style>
    </div>
  );
}
